import type { SeatMapView } from '@cineamo/ui/src/room-3d/room-design';

export type SeatMapViewMode = '2D' | '3D';

export const SEAT_MAP_VIEW_MODE_KEY = 'seat-map-view-mode';

export function parseSeatMapViewMode(raw: string | null | undefined): SeatMapViewMode {
    return raw === '3D' ? '3D' : '2D';
}

export function readSeatMapViewMode(storage: Pick<Storage, 'getItem'> | undefined): SeatMapViewMode {
    try {
        return parseSeatMapViewMode(storage?.getItem(SEAT_MAP_VIEW_MODE_KEY));
    } catch {
        return '2D';
    }
}

export function writeSeatMapViewMode(storage: Pick<Storage, 'setItem'> | undefined, mode: SeatMapViewMode): void {
    try {
        storage?.setItem(SEAT_MAP_VIEW_MODE_KEY, mode);
    } catch {
        // Private windows and blocked site data: the choice just isn't remembered.
    }
}

export function resolveSeatMapViewMode(stored: SeatMapViewMode, isWebGLSupported: boolean): SeatMapViewMode {
    return isWebGLSupported ? stored : '2D';
}

/**
 * Which seat map a guest starts in, in precedence order: no WebGL, then the room's `'2d-only'`, then a choice made
 * during this visit, then the room's own `'3d'`/`'2d'`, then `fallback` (the experiment assignment or stored choice).
 */
export function pickSeatMapViewMode(input: {
    roomView: SeatMapView | null;
    isWebGLSupported: boolean;
    chosen: SeatMapViewMode | null;
    fallback: SeatMapViewMode;
}): SeatMapViewMode {
    const { roomView, isWebGLSupported, chosen, fallback } = input;
    if (!isWebGLSupported || roomView === '2d-only') return '2D';
    if (chosen) return chosen;
    if (roomView === '3d') return '3D';
    if (roomView === '2d') return '2D';
    return fallback;
}

/** Whether the guest may switch views at all: needs WebGL, and the room must not be `'2d-only'`. */
export function canToggleSeatMapView(roomView: SeatMapView | null, isWebGLSupported: boolean): boolean {
    return isWebGLSupported && roomView !== '2d-only';
}
